import { ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ElementRef, EventEmitter, Output, inject} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import {DisparoAutomaticoService} from '../../../../shared/service/disparo-automatico.service';
import {SocketService} from '../../../../shared/service/socket.service';
import {DisparoContatoAtualizacaoSocket} from '../../../../shared/model/socket/disparo-contato-atualizacao-socket.model';
import {ListaContatosModel} from '../../../../shared/model/disparo-automatico/lista-contatos.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {CelularMaskPipe} from '../../../../shared/pipe/celular-mask.pipe';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {BalaoInformativoComponent} from '../../../../shared/components/balao-informativo/balao-informativo.component';
import { MethodsUtils } from '../../../../shared/util/methods.utils';
import { TitleCasePipe } from '@angular/common';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { CpfCnpjMaskPipe } from '../../../../shared/pipe/cpf-cnpj-mask.pipe';
import {TabelaSkeletonComponent} from '../../../../shared/components/tabela-skeleton/tabela-skeleton.component';
import {MatDialog} from '@angular/material/dialog';
import {ModalBaseCompletaComponent} from '../modal-base-completa/modal-base-completa.component';
import {ButtonPadraoComponent} from '../../../../shared/components/button-padrao/button-padrao.component';
import {iconeStatusContatoDisparo, iconeIntegracaoContatoDisparo} from '../../../../shared/model/disparo-automatico/icone-contato-disparo.util';
import { DisparoAutomaticoVariavelModel } from '../../../../shared/model/disparo-automatico/disparo-automatico-variavel.model';
import { TemplateSalvamentoModel } from '../../../../shared/model/template/template-salvamento-model';

@Component({
  selector: 'app-lista-contatos',
  standalone: true,
  imports: [
    MatTableModule,
    CelularMaskPipe,
    MatTooltip,
    MatIcon,
    BalaoInformativoComponent,
    TitleCasePipe,
    MatProgressSpinner,
    CpfCnpjMaskPipe,
    TabelaSkeletonComponent,
    ButtonPadraoComponent
],
  templateUrl: './lista-contatos.component.html',
  styleUrl: './lista-contatos.component.css'
})
export class ListaContatosComponent implements OnChanges, OnInit {
  @Input() anexo: any;
  @Input() disparoId: number | null = null;
  @Input() statusDisparo?: string;
  @Input() tipoDisparo?: string;
  @Input() temIntegracao = false;
  @Input() templateSms?: TemplateSalvamentoModel;
  @Input() variaveis: DisparoAutomaticoVariavelModel[] = [];
  @Input() exibirReenvioIntegracao = false;
  @Input() reenviandoIntegracao = false;
  @Output() contatosCarregados = new EventEmitter<ListaContatosModel[]>();
  @Output() reenviarIntegracao = new EventEmitter<void>();

  protected readonly iconeStatusContato = iconeStatusContatoDisparo;
  protected readonly iconeIntegracaoContato = iconeIntegracaoContatoDisparo;
  @ViewChild('tableContainer') tableContainerRef!: ElementRef<HTMLDivElement>;
  listaContatos: ListaContatosModel[] = [];
  colunasComplementares: string[] = [];
  listaContatosTable = new MatTableDataSource<ListaContatosModel>();
  quantidadeContatos?: number;
  quantidadeListaContatosValidos?: number;
  displayedColumns: string[] = ['contatoLido', 'nomeContato', 'cpf', 'telefone', 'email'];

  fimDaLista = false;
  isCarregando = false;
  temMaisPaginas = false;
  private paginaAtual = 0;
  private readonly PAGE_SIZE = 50;
  private readonly PREVIEW_SIZE = 5;
  private readonly destroyRef = inject(DestroyRef);
  private readonly STATUS_SEM_SOCKET_CONFIAVEL = ['Processando', 'Limite atingido', 'Em fila', 'Buscando base'];
  private atualizandoPreview = false;

  private readonly disparoAutomaticoService = inject(DisparoAutomaticoService);
  private readonly dialog = inject(MatDialog);
  private readonly socketService = inject(SocketService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.socketService.escutarAtualizacaoContatoDisparo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(atualizacao => this.aplicarAtualizacaoContato(atualizacao));

    interval(6000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.disparoId && this.STATUS_SEM_SOCKET_CONFIAVEL.includes(this.statusDisparo ?? '')) {
          this.atualizarPreviewDoBackend();
        }
      });
  }

  private atualizarPreviewDoBackend(): void {
    if (!this.disparoId || this.atualizandoPreview) return;
    this.atualizandoPreview = true;

    this.disparoAutomaticoService
      .buscarContatosPorDisparo(this.disparoId, 0, this.PAGE_SIZE)
      .subscribe({
        next: (res: any) => {
          const atualizados: ListaContatosModel[] = res.content ?? [];
          atualizados.forEach(contato => {
            contato.isContatoValido = this.validarTelefone(contato.telefone!);
            contato.isEmailValido = this.validarEmail(contato.email!);
            contato.isCpfValido = this.validarCpf(contato.cpf!);
          });

          const atualizadosPorId = new Map(atualizados.map(c => [c.id, c]));
          this.listaContatos = this.listaContatos.map(c => atualizadosPorId.get(c.id) ?? c);
          atualizados.forEach(c => {
            if (!this.listaContatos.some(existente => existente.id === c.id)) {
              this.listaContatos.push(c);
            }
          });

          this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
          this.quantidadeListaContatosValidos = this.listaContatos.filter(ct => ct.isContatoValido).length;
          this.cdr.detectChanges();
        },
        complete: () => { this.atualizandoPreview = false; },
        error: () => { this.atualizandoPreview = false; }
      });
  }

  private aplicarAtualizacaoContato(atualizacao: DisparoContatoAtualizacaoSocket): void {
    if (!this.disparoId || atualizacao.disparoId !== this.disparoId) return;
    const index = this.listaContatos.findIndex(c => c.id === atualizacao.contatoId);
    if (index === -1) return;

    const contatoAtualizado: ListaContatosModel = { ...this.listaContatos[index] };
    if (atualizacao.status !== undefined) contatoAtualizado.status = atualizacao.status;
    if (atualizacao.statusEnvio !== undefined) contatoAtualizado.statusEnvio = atualizacao.statusEnvio;
    if (atualizacao.statusEnvioIntegracao !== undefined) contatoAtualizado.statusEnvioIntegracao = atualizacao.statusEnvioIntegracao;
    if (atualizacao.motivoErro !== undefined) contatoAtualizado.motivoErro = atualizacao.motivoErro;
    if (atualizacao.motivoErroIntegracao !== undefined) contatoAtualizado.motivoErroIntegracao = atualizacao.motivoErroIntegracao;
    contatoAtualizado.atualizadoAgora = true;

    this.listaContatos = [
      ...this.listaContatos.slice(0, index),
      contatoAtualizado,
      ...this.listaContatos.slice(index + 1)
    ];
    this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
    this.cdr.detectChanges();

    setTimeout(() => {
      contatoAtualizado.atualizadoAgora = false;
      this.cdr.detectChanges();
    }, 900);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['disparoId'] && this.disparoId) {
      this.carregarDoBackend(true);
    } else if (changes['anexo'] && this.anexo && !this.disparoId) {
      this.carregarDoAnexo();
    } else if (changes['anexo'] && !this.anexo && !this.disparoId) {
      this.limparLista();
    } else if (changes['tipoDisparo'] && this.listaContatos.length) {
      this.revalidarTelefones();
    }
  }

  private limparLista(): void {
    this.listaContatos = [];
    this.listaContatosTable.data = [];
    this.colunasComplementares = [];
    this.displayedColumns = ['contatoLido', 'nomeContato', 'cpf', 'telefone', 'email'];
    this.quantidadeContatos = undefined;
    this.quantidadeListaContatosValidos = undefined;
    this.fimDaLista = false;
  }

  private carregarDoAnexo(): void {
    this.listaContatos = this.anexo.listaContatos ?? [];
    this.colunasComplementares = this.anexo.colunasComplementares ?? [];
    this.displayedColumns = ['contatoLido', 'nomeContato', 'cpf', 'telefone', 'email'];
    this.listaContatos.forEach(contato => {
      contato.telefone = this.normalizarTelefoneSms(contato.telefone);
      contato.isContatoValido = this.validarTelefone(contato.telefone!);
      contato.isEmailValido = this.validarEmail(contato.email!);
      contato.isCpfValido = this.validarCpf(contato.cpf!);
    });

    this.quantidadeContatos = this.listaContatos.length;
    this.quantidadeListaContatosValidos = this.listaContatos.filter(ct => ct.isContatoValido).length;

    this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
    this.fimDaLista = true;
    this.temMaisPaginas = false;
  }

  private carregarDoBackend(reset: boolean): void {
    if (this.isCarregando || this.fimDaLista || !this.disparoId) return;

    if (reset) {
      this.paginaAtual = 0;
      this.fimDaLista = false;
      this.listaContatos = [];
      this.listaContatosTable.data = [];
    }

    this.isCarregando = true;

    this.disparoAutomaticoService
      .buscarContatosPorDisparo(this.disparoId, this.paginaAtual, this.PAGE_SIZE)
      .subscribe({
        next: (res: any) => {
          const novosContatos: ListaContatosModel[] = res.content ?? [];

          novosContatos.forEach(contato => {
            contato.isContatoValido = this.validarTelefone(contato.telefone!);
            contato.isEmailValido = this.validarEmail(contato.email!);
            contato.isCpfValido = this.validarCpf(contato.cpf!);
          });

          if (reset) {
            this.colunasComplementares = this.anexo?.colunasComplementares ?? [];
            this.displayedColumns = ['contatoLido', 'nomeContato', 'cpf', 'telefone', 'email'];
            this.quantidadeContatos = res.totalElements;
          }

          this.listaContatos = [...this.listaContatos, ...novosContatos];
          this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
          if (reset) {
            this.contatosCarregados.emit(novosContatos);
          }
          this.quantidadeListaContatosValidos = this.listaContatos.filter(ct => ct.isContatoValido).length;
          this.temMaisPaginas = !res.last;
          this.fimDaLista = res.last;
          this.paginaAtual++;
        },
        error: () => { this.isCarregando = false; },
        complete: () => { this.isCarregando = false; }
      });
  }

  onScroll(): void {
    const element = this.tableContainerRef?.nativeElement;
    if (!element || this.isCarregando || this.fimDaLista) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      this.carregarDoBackend(false);
    }
  }

  manipularInformacoesAnexo(){
    this.listaContatos = this.anexo.listaContatos;
    this.colunasComplementares = this.anexo.colunasComplementares ?? [];
    this.displayedColumns = ['contatoLido', 'nomeContato', 'cpf', 'telefone', 'email'];
    this.listaContatos.forEach(contato => {
      contato.isContatoValido = this.validarTelefone(contato.telefone!);
      contato.isEmailValido = this.validarEmail(contato.email!);
      contato.isCpfValido = this.validarCpf(contato.cpf!);
    });
    this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
    this.quantidadeContatos = this.listaContatos.length;
    this.quantidadeListaContatosValidos = this.listaContatos.filter(ct => ct.isContatoValido).length;
  }

  validarTelefone(telefone: string): boolean {
    if (!telefone) {
      return false;
    }
    const telefoneNumerico = telefone.replace(/\D/g, '');
    if (this.tipoDisparo === 'sms') {
      return /^55\d{2}9\d{8}$/.test(telefoneNumerico);
    }
    if (telefoneNumerico.length >= 12 && telefoneNumerico.length <= 13) {
      return true;
    }
    return false;
  }

  private revalidarTelefones(): void {
    this.listaContatos.forEach(contato => {
      contato.telefone = this.normalizarTelefoneSms(contato.telefone);
      contato.isContatoValido = this.validarTelefone(contato.telefone!);
    });
    this.listaContatosTable.data = this.listaContatos.slice(0, this.PREVIEW_SIZE);
  }

  private normalizarTelefoneSms(telefone?: string): string | undefined {
    if (this.tipoDisparo !== 'sms' || !telefone) return telefone;
    let digitos = telefone.replace(/\D/g, '');
    if (digitos.length === 10) {
      digitos = `55${digitos.slice(0, 2)}9${digitos.slice(2)}`;
    } else if (digitos.length === 11) {
      digitos = `55${digitos}`;
    } else if (digitos.length === 12 && digitos.startsWith('55')) {
      digitos = `${digitos.slice(0, 4)}9${digitos.slice(4)}`;
    }
    return digitos;
  }

  validarEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    const emailTrimmed = email.trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  }

  validarCpf(cpf: string): boolean {
    if(!cpf) {
      return false;
    }
    return MethodsUtils.validarCPFCNPJ(cpf);
  }

  buscarCampoComplementar(elemento: any, coluna: string): string {
    return elemento.camposComplementares?.find((c: any) => c.nome === coluna)?.valor ?? '--';
  }

  abrirBaseCompleta(): void {
    this.dialog.open(ModalBaseCompletaComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '100vh',
      position: {top: '0', right: '0'},
      panelClass: 'slide-modal',
      data: {
        contatos: this.disparoId ? undefined : this.listaContatos,
        colunasComplementares: this.colunasComplementares,
        disparoId: this.disparoId,
        totalContatos: this.quantidadeContatos,
        tipoDisparo: this.tipoDisparo,
        temIntegracao: this.temIntegracao,
        templateSms: this.templateSms,
        variaveis: this.variaveis
      }
    });
  }
}
